import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listAllDocuments, getDocumentDetails, getReceiptPdfLink, getDocumentPdfLink } from '../api/client';
import { getRememberedRivhitDocumentLinkForCategory } from '../lib/rivhitDocumentsSync';
import { useRivhitData, type RivhitCustomer } from '../context/RivhitDataContext';
import { useAuth } from '../context/AuthContext';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import DebtReportSendModal from '../components/DebtReportSendModal';
import { contactedThisMonth } from '../lib/collectionContact';
import { workDate } from '../lib/customerWork';
import { ReconciliationWarning } from '../components/CustomerReconciliationPanel';
import type { CustomerActivity, CustomerReconciliation } from '../api/client';
import DocumentWhatsAppModal from '../components/DocumentWhatsAppModal';
import { printCustomerReport } from '../lib/printCustomerReport';
import RivhitSyncBanner from '../components/RivhitSyncBanner';
import { useRivhitSyncStatus } from '../hooks/useRivhitSyncStatus';
import {
  clearRivhitDocumentsDirty,
  getRivhitDocumentsDirtyAt,
  subscribeToRivhitDocumentsDirty,
} from '../lib/rivhitDocumentsSync';

interface ClosingLink {
  document_category: string;
  document_type: number;
  document_number: number;
  amount: number;
  close_status: string;
}

interface UnifiedDocumentItem {
  view_type?: 'document' | 'customer';
  document_category: string;
  document_type: number;
  document_type_name: string;
  document_number: number;
  document_date: string;
  document_time?: string;
  amount: number;
  paid_amount?: number;
  open_amount?: number;
  balance_status?: 'provider' | 'local_overlay' | 'closure_metadata' | 'unknown';
  last_collection_contact_at?: string;
  last_collection_contact_channel?: string;
  reconciliation?: CustomerReconciliation;
  invoices_count?: number;
  customer_id?: number;
  customer_name: string;
  last_interaction_at?: string;
  last_interaction_type?: string;
  last_interaction_channel?: string;
  last_service_activity?: CustomerActivity | null;
  document_link?: string;
  is_closed?: boolean;
  is_cancelled?: boolean;
  closed_by?: ClosingLink[];
  closing_documents?: ClosingLink[];
  documents?: UnifiedDocumentItem[];
}

interface DocumentLineItem {
  item_id?: number;
  description?: string;
  catalog_number?: string;
  quantity: number;
  price_nis?: number;
  total_nis?: number;
}

interface DocumentVatSummary {
  subtotal?: number;
  vat_rate?: number;
  vat_amount?: number;
  total?: number;
}

interface DocumentsSummary {
  total_invoices_amount: number;
  total_receipts_amount: number;
  total_invoice_receipts_amount: number;
  total_invoice_receipts_count: number;
  total_open_amount: number;
  total_open_invoices_count: number;
  uncertain_open_invoices_count: number;
  uncertain_document_numbers: number[];
  total_delivery_notes_amount: number;
  total_delivery_notes_count: number;
  total_open_delivery_notes_count: number;
  total_closed_delivery_notes_count: number;
}

interface DocumentsPageData {
  items?: UnifiedDocumentItem[];
  total_count?: number;
  offset?: number;
  has_more?: boolean;
  summary?: DocumentsSummary;
}

interface DocumentsPageCacheEntry {
  items: UnifiedDocumentItem[];
  total_count: number;
  has_more: boolean;
  summary: DocumentsSummary;
  cached_at: number;
}

interface WhatsAppCustomerTarget {
  customerId: number;
  displayName?: string;
  phone?: string | null;
  openAmount?: number;
}

type CategoryFilter = 'all' | 'invoice' | 'receipt' | 'invoice_receipt' | 'delivery_note' | 'quote' | 'order' | 'credit_invoice' | 'return_note';
type ViewMode = 'documents' | 'customer';
type LastInteractionFilter = 'all' | 'range' | 'none';
type DocumentSortField = 'document_number' | 'document_date' | 'customer_name' | 'amount' | 'open_amount' | 'invoices_count';
type SortDirection = 'asc' | 'desc';
const DOCUMENTS_PAGE_SIZE = 100;
const LOCAL_DOCUMENTS_CACHE_PREFIX = 'documents_page_cache_v3';
const LOCAL_DOCUMENTS_CACHE_TTL_MS = 2 * 60 * 1000;

const CATEGORY_BADGES: Record<string, { bg: string; text: string; labelKey: string }> = {
  invoice: { bg: 'bg-blue-100', text: 'text-blue-700', labelKey: 'documents.categoryInvoice' },
  receipt: { bg: 'bg-green-100', text: 'text-green-700', labelKey: 'documents.categoryReceipt' },
  invoice_receipt: { bg: 'bg-purple-100', text: 'text-purple-700', labelKey: 'documents.categoryInvoiceReceipt' },
  delivery_note: { bg: 'bg-orange-100', text: 'text-orange-700', labelKey: 'documents.categoryDeliveryNote' },
  quote: { bg: 'bg-amber-100', text: 'text-amber-700', labelKey: 'documents.categoryQuote' },
  order: { bg: 'bg-cyan-100', text: 'text-cyan-700', labelKey: 'documents.categoryOrder' },
  credit_invoice: { bg: 'bg-rose-100', text: 'text-rose-700', labelKey: 'documents.categoryCreditInvoice' },
  return_note: { bg: 'bg-pink-100', text: 'text-pink-700', labelKey: 'documents.categoryReturnNote' },
};

function toIsoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayIsoDate(): string {
  return toIsoDate(new Date());
}

function getDaysAgoIsoDate(daysAgo: number): string {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return toIsoDate(date);
}

function getStartOfWeek(): string {
  const now = new Date();
  const day = now.getDay();
  const diff = day; // Sunday as start of week
  const start = new Date(now);
  start.setDate(now.getDate() - diff);
  return toIsoDate(start);
}

function getStartOfMonth(): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
}

function getStartOfPreviousMonth(): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
}

function getEndOfPreviousMonth(): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 0));
}

function getMonthsAgoIsoDate(months: number): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth() - months, now.getDate()));
}

function getStartOfYearIsoDate(): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), 0, 1));
}

function localDateBoundaryIso(isoDate: string, nextDay = false): string | undefined {
  if (!isoDate) return undefined;
  const [year, month, day] = isoDate.split('-').map(Number);
  const boundary = new Date(year, month - 1, day);
  if (nextDay) boundary.setDate(boundary.getDate() + 1);
  return boundary.toISOString();
}

function parseDocumentDate(value: string): number {
  const normalized = value.replace(/\//g, '');
  if (normalized.length !== 8) return 0;
  const day = normalized.slice(0, 2);
  const month = normalized.slice(2, 4);
  const year = normalized.slice(4, 8);
  return Number(`${year}${month}${day}`);
}

function parseDocumentTime(value?: string): number {
  if (!value) return 0;
  const normalized = value.replace(/:/g, '').trim();
  const digits = normalized.replace(/\D/g, '');
  if (!digits) return 0;
  return Number(digits.padEnd(6, '0').slice(0, 6));
}

function emptyDocumentsSummary(): DocumentsSummary {
  return {
    total_invoices_amount: 0,
    total_receipts_amount: 0,
    total_invoice_receipts_amount: 0,
    total_invoice_receipts_count: 0,
    total_open_amount: 0,
    total_open_invoices_count: 0,
    uncertain_open_invoices_count: 0,
    uncertain_document_numbers: [],
    total_delivery_notes_amount: 0,
    total_delivery_notes_count: 0,
    total_open_delivery_notes_count: 0,
    total_closed_delivery_notes_count: 0,
  };
}

function normalizeSummary(summary?: Partial<DocumentsSummary>): DocumentsSummary {
  return {
    ...emptyDocumentsSummary(),
    ...(summary ?? {}),
  };
}

function stableCacheParamString(params: Record<string, unknown>): string {
  return JSON.stringify(
    Object.keys(params)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        const value = params[key];
        if (value !== undefined) acc[key] = value;
        return acc;
      }, {}),
  );
}

function documentsLocalCacheKey(tenantId: string | null | undefined, params: Record<string, unknown>): string {
  return `${LOCAL_DOCUMENTS_CACHE_PREFIX}:${tenantId || 'global'}:${stableCacheParamString(params)}`;
}

function readDocumentsLocalCache(key: string): DocumentsPageCacheEntry | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DocumentsPageCacheEntry;
    if (!parsed || !Array.isArray(parsed.items) || typeof parsed.cached_at !== 'number') return null;
    if (Date.now() - parsed.cached_at > LOCAL_DOCUMENTS_CACHE_TTL_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeDocumentsLocalCache(key: string, entry: Omit<DocumentsPageCacheEntry, 'cached_at'>): void {
  try {
    localStorage.setItem(key, JSON.stringify({ ...entry, cached_at: Date.now() }));
  } catch {
    // Browsers can reject writes when storage is full or disabled; in-memory cache still works.
  }
}

function clearTenantDocumentsLocalCache(tenantId: string | null | undefined): void {
  try {
    const prefix = `${LOCAL_DOCUMENTS_CACHE_PREFIX}:${tenantId || 'global'}:`;
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignore storage access failures.
  }
}

export default function Documents() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    configured,
    customers,
    ensureCustomersLoaded,
  } = useRivhitData();

  const refreshPageDataRef = useRef<() => Promise<void>>();
  const syncState = useRivhitSyncStatus(
    () => { refreshPageDataRef.current?.(); },
    { enabled: configured },
  );

  // Data
  const [documents, setDocuments] = useState<UnifiedDocumentItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageOffset, setPageOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [serviceError, setServiceError] = useState<string | null>(null);
  const [summary, setSummary] = useState<DocumentsSummary>(emptyDocumentsSummary);
  const [whatsAppCustomer, setWhatsAppCustomer] = useState<WhatsAppCustomerTarget | null>(null);
  const [whatsAppDocument, setWhatsAppDocument] = useState<UnifiedDocumentItem | null>(null);
  const pageCacheRef = useRef<Map<string, DocumentsPageCacheEntry>>(new Map());
  const prefetchCacheRef = useRef<Map<string, DocumentsPageCacheEntry>>(new Map());

  // Filters
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('documents');
  const [openOnly, setOpenOnly] = useState(false);
  const [sortField, setSortField] = useState<DocumentSortField>('document_date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [customerSearch, setCustomerSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(getTodayIsoDate);
  const [dateTo, setDateTo] = useState(getTodayIsoDate);
  const [activeRangeKey, setActiveRangeKey] = useState<string | null>('today');
  const [lastInteractionFilter, setLastInteractionFilter] = useState<LastInteractionFilter>('all');
  const [contactSort, setContactSort] = useState<'' | 'asc' | 'desc'>('');
  const [calendarNow, setCalendarNow] = useState(() => new Date());
  useEffect(() => {
    const update = () => setCalendarNow(new Date());
    const timer = setInterval(update, 60000);
    window.addEventListener('focus', update);
    return () => { clearInterval(timer); window.removeEventListener('focus', update); };
  }, []);
  const [lastInteractionFrom, setLastInteractionFrom] = useState(getTodayIsoDate);
  const [lastInteractionTo, setLastInteractionTo] = useState(getTodayIsoDate);
  const [selectedCustomer, setSelectedCustomer] = useState<RivhitCustomer | null>(null);
  const [expandedCustomers, setExpandedCustomers] = useState<Set<number>>(new Set());
  const [customerDocuments, setCustomerDocuments] = useState<Record<number, UnifiedDocumentItem[]>>({});
  const [customerDocumentsLoading, setCustomerDocumentsLoading] = useState<Set<number>>(new Set());

  // Document row expand state (for document view mode)
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [docLineItems, setDocLineItems] = useState<Record<string, DocumentLineItem[]>>({});
  const [docVatSummary, setDocVatSummary] = useState<Record<string, DocumentVatSummary>>({});
  const [docLineItemsLoading, setDocLineItemsLoading] = useState<Set<string>>(new Set());

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const loadGenerationRef = useRef(0);
  const handledDirtyAtRef = useRef<number | null>(null);
  // Cache keys for which we've already auto-triggered a forced sync after an
  // empty result, so a genuinely empty date range doesn't loop forever.
  const autoSyncedKeysRef = useRef<Set<string>>(new Set());

  // Eagerly clear all caches on mount when a dirty flag is pending so the
  // first debounced loadDocuments() call hits the API instead of serving stale
  // localStorage / in-memory data.
  const didEarlyCacheClearRef = useRef(false);
  if (!didEarlyCacheClearRef.current) {
    didEarlyCacheClearRef.current = true;
    const dirtyAt = getRivhitDocumentsDirtyAt();
    if (dirtyAt) {
      pageCacheRef.current.clear();
      prefetchCacheRef.current.clear();
      clearTenantDocumentsLocalCache(user?.tenant_id);
      console.log('[Documents] 🧹 Eager cache clear on mount — dirty flag detected', { dirtyAt, timestamp: new Date().toISOString() });
    }
  }

  const formatDateForApi = (isoDate: string): string | undefined => {
    if (!isoDate) return undefined;
    const [y, m, d] = isoDate.split('-');
    return `${d}${m}${y}`;
  };

  const handleDateFromChange = (nextFrom: string) => {
    setDateFrom(nextFrom);
    setActiveRangeKey(null);
    if (dateTo && nextFrom > dateTo) {
      setDateTo(nextFrom);
    }
  };

  const handleDateToChange = (nextTo: string) => {
    setDateTo(nextTo);
    setActiveRangeKey(null);
    if (dateFrom && nextTo < dateFrom) {
      setDateFrom(nextTo);
    }
  };

  const handleLastInteractionFromChange = (nextFrom: string) => {
    setLastInteractionFrom(nextFrom);
    if (lastInteractionTo && nextFrom > lastInteractionTo) {
      setLastInteractionTo(nextFrom);
    }
  };

  const handleLastInteractionToChange = (nextTo: string) => {
    setLastInteractionTo(nextTo);
    if (lastInteractionFrom && nextTo < lastInteractionFrom) {
      setLastInteractionFrom(nextTo);
    }
  };

  const mergeRememberedLinksStable = useCallback((item: UnifiedDocumentItem): UnifiedDocumentItem => {
    const rememberedLink = getRememberedRivhitDocumentLinkForCategory(
      item.document_category,
      item.document_type,
      item.document_number,
    );
    return {
      ...item,
      document_link: item.document_link || rememberedLink,
      documents: item.documents?.map((d) => ({
        ...d,
        document_link: d.document_link || getRememberedRivhitDocumentLinkForCategory(
          d.document_category,
          d.document_type,
          d.document_number,
        ),
      })),
    };
  }, []);

  const buildParams = useCallback(() => {
    const params: Record<string, unknown> = { include_service_activity: true };
    if (contactSort) params.contact_sort = contactSort;
    if (categoryFilter !== 'all') params.category_filter = categoryFilter;
    if (customerSearch.trim()) params.search = customerSearch.trim();
    if (openOnly) params.open_only = true;
    if (lastInteractionFilter !== 'all') {
      params.last_interaction_filter = lastInteractionFilter;
    }
    if (lastInteractionFilter === 'range') {
      const interactionFrom = localDateBoundaryIso(lastInteractionFrom);
      const interactionTo = localDateBoundaryIso(lastInteractionTo, true);
      if (interactionFrom) params.last_interaction_from = interactionFrom;
      if (interactionTo) params.last_interaction_to = interactionTo;
    }
    if (viewMode === 'customer') params.group_by = 'customer';
    if (customerSearch.trim()) {
      // Paginate search like browsing instead of pulling 10000 in one shot.
      // The server caps PDF-link backfill per request to stay under the
      // API Gateway 29s timeout; large unpaginated responses were silently
      // truncated to 504 (see invoicing._backfill_page_item_links).
      const fd = formatDateForApi(dateFrom);
      const td = formatDateForApi(dateTo);
      if (fd) params.from_date = fd;
      if (td) params.to_date = td;
      params.limit = DOCUMENTS_PAGE_SIZE;
      params.offset = pageOffset;
    } else if (viewMode === 'customer') {
      // Customer view: fetch all documents in the time range (no pagination)
      const fd = formatDateForApi(dateFrom);
      const td = formatDateForApi(dateTo);
      if (fd) params.from_date = fd;
      if (td) params.to_date = td;
      params.limit = 10000;
      params.offset = 0;
    } else {
      const fd = formatDateForApi(dateFrom);
      const td = formatDateForApi(dateTo);
      if (fd) params.from_date = fd;
      if (td) params.to_date = td;
      params.limit = DOCUMENTS_PAGE_SIZE;
      params.offset = pageOffset;
    }
    return params;
  }, [
    categoryFilter,
    contactSort,
    customerSearch,
    dateFrom,
    dateTo,
    lastInteractionFilter,
    lastInteractionFrom,
    lastInteractionTo,
    openOnly,
    pageOffset,
    viewMode,
  ]);

  const loadDocuments = useCallback(async (force = false) => {
    setLoading(true);
    setSummaryLoading(true);
    setServiceError(null);
    const generation = ++loadGenerationRef.current;

    const t0 = performance.now();
    console.log(`[Documents] 📄 loadDocuments start (gen=${generation}) force=${force}`, { timestamp: new Date().toISOString() });

    try {
      const params = buildParams();
      const currentOffset = (params.offset as number) ?? 0;
      const localCacheKey = documentsLocalCacheKey(user?.tenant_id, params);

      // Check page cache first, then prefetch cache (bypass if force=true)
      const pageCached = !force ? pageCacheRef.current.get(localCacheKey) : undefined;
      const prefetched = !force && !pageCached ? prefetchCacheRef.current.get(localCacheKey) : undefined;
      let mergedItems: UnifiedDocumentItem[];
      let data: DocumentsPageData;

      if (pageCached) {
        console.log(`[Documents] ⚡ Using page cache at offset=${currentOffset}`);
        mergedItems = pageCached.items;
        data = {
          items: pageCached.items,
          total_count: pageCached.total_count,
          has_more: pageCached.has_more,
          summary: pageCached.summary,
        };
      } else if (prefetched) {
        console.log(`[Documents] ⚡ Using prefetched page at offset=${currentOffset}`);
        mergedItems = prefetched.items;
        data = {
          items: prefetched.items,
          total_count: prefetched.total_count,
          has_more: prefetched.has_more,
          summary: prefetched.summary,
        };
        prefetchCacheRef.current.delete(localCacheKey);
        // Store in page cache
        pageCacheRef.current.set(localCacheKey, prefetched);
      } else {
        const localCached = !force ? readDocumentsLocalCache(localCacheKey) : null;
        if (localCached) {
          console.log(`[Documents] ⚡ Using local cache at offset=${currentOffset}`);
          mergedItems = localCached.items;
          data = {
            items: localCached.items,
            total_count: localCached.total_count,
            has_more: localCached.has_more,
            summary: localCached.summary,
          };
          pageCacheRef.current.set(localCacheKey, localCached);
        } else {
          const res = await listAllDocuments({
            ...(params as Record<string, string | number | boolean>),
            ...(force && { sync: true }),
          });
          // Discard stale response if filters changed while awaiting
          if (generation !== loadGenerationRef.current) {
            console.log(`[Documents] 🚫 Discarding stale response (gen=${generation}, current=${loadGenerationRef.current})`);
            return;
          }
          const t1 = performance.now();
          data = res.data;
          mergedItems = (data.items ?? []).map(mergeRememberedLinksStable);
          console.log(`[Documents] ✅ loadDocuments API returned in ${(t1 - t0).toFixed(1)}ms — ${mergedItems.length} items, total=${data.total_count ?? 0}, offset=${data.offset ?? 0}, hasMore=${Boolean(data.has_more)}`, { timestamp: new Date().toISOString() });
          // Store in caches — but never persist an empty/zero snapshot. A cold
          // or stale server-side index returns 0 documents while a background
          // Rivhit sync is still running; caching that would "stick" the empty
          // page for the cache TTL until the user manually pressed Refresh.
          const hasData = mergedItems.length > 0 || (data.total_count ?? 0) > 0;
          if (hasData) {
            const cacheEntry = {
              items: mergedItems,
              total_count: data.total_count ?? 0,
              has_more: Boolean(data.has_more),
              summary: normalizeSummary(data.summary),
              cached_at: Date.now(),
            };
            pageCacheRef.current.set(localCacheKey, cacheEntry);
            writeDocumentsLocalCache(localCacheKey, {
              items: cacheEntry.items,
              total_count: cacheEntry.total_count,
              has_more: cacheEntry.has_more,
              summary: cacheEntry.summary,
            });
          }
        }
      }

      setDocuments(mergedItems);
      setTotalCount(data.total_count ?? 0);
      setHasMore(Boolean(data.has_more));
      setSummary(normalizeSummary(data.summary));

      // Auto-recover from a cold/stale server index. A non-forced request reads
      // DynamoDB immediately and only fires the Rivhit sync in the background,
      // so the first open after the cache expired returns 0 documents and zeroed
      // KPIs. Force one synced refetch (the same path as the Refresh button) so
      // the page populates on its own. Guard per cache key so a genuinely empty
      // date range triggers at most one forced sync instead of looping.
      const resultIsEmpty = mergedItems.length === 0 && (data.total_count ?? 0) === 0;
      if (!force && resultIsEmpty && !autoSyncedKeysRef.current.has(localCacheKey)) {
        autoSyncedKeysRef.current.add(localCacheKey);
        console.log('[Documents] 🔁 Empty snapshot on load — auto-forcing a synced refresh', { offset: currentOffset, timestamp: new Date().toISOString() });
        // The forced refresh increments the load generation, so this call's
        // `finally` will skip flipping loading off — the spinner stays up until
        // the synced refetch resolves.
        void refreshPageDataRef.current?.();
        return;
      }

      // Prefetch next page in background (skip for customer view — it fetches all)
      if (data.has_more && viewMode !== 'customer') {
        const nextOffset = currentOffset + DOCUMENTS_PAGE_SIZE;
        const nextParams = { ...params, offset: nextOffset };
        const nextCacheKey = documentsLocalCacheKey(user?.tenant_id, nextParams);
        if (!pageCacheRef.current.has(nextCacheKey) && !prefetchCacheRef.current.has(nextCacheKey)) {
          listAllDocuments(nextParams as Record<string, string | number | boolean>)
            .then((nextRes) => {
              // Discard prefetch if filters changed
              if (generation !== loadGenerationRef.current) return;
              const nextItems = (nextRes.data.items ?? []).map(mergeRememberedLinksStable);
              const entry = {
                items: nextItems,
                total_count: nextRes.data.total_count ?? 0,
                has_more: Boolean(nextRes.data.has_more),
                summary: normalizeSummary(nextRes.data.summary),
                cached_at: Date.now(),
              };
              prefetchCacheRef.current.set(nextCacheKey, entry);
              pageCacheRef.current.set(nextCacheKey, entry);
              writeDocumentsLocalCache(nextCacheKey, {
                items: entry.items,
                total_count: entry.total_count,
                has_more: entry.has_more,
                summary: entry.summary,
              });
              console.log(`[Documents] 🔮 Prefetched next page offset=${nextOffset} — ${nextItems.length} items`);
            })
            .catch(() => { /* silently ignore prefetch failures */ });
        }
      }
    } catch (err) {
      if (generation !== loadGenerationRef.current) return;
      const status = (err as any)?.response?.status;
      if (status === 429 || status === 503) {
        setServiceError(t('documents.serviceUnavailable', 'The accounting service is temporarily unavailable. Your documents could not be loaded — please try again shortly.'));
      } else if (status && status >= 500) {
        setServiceError(t('documents.serverError', 'The accounting service encountered an error. Please try again in a moment.'));
      } else {
        setServiceError(t('documents.loadError', 'Documents could not be loaded. Check your connection and try again.'));
      }
      console.error(`[Documents] ❌ loadDocuments failed after ${(performance.now() - t0).toFixed(1)}ms:`, err, { timestamp: new Date().toISOString() });
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoading(false);
        setSummaryLoading(false);
      }
      console.log(`[Documents] 🏁 loadDocuments complete (gen=${generation}) — total elapsed ${(performance.now() - t0).toFixed(1)}ms`, { timestamp: new Date().toISOString() });
    }
  }, [buildParams, customerSearch, mergeRememberedLinksStable, user?.tenant_id, viewMode]);

  const refreshDocuments = useCallback(async (force = false) => {
    if (force) {
      pageCacheRef.current.clear();
      prefetchCacheRef.current.clear();
      clearTenantDocumentsLocalCache(user?.tenant_id);
    }
    await loadDocuments(force);
  }, [loadDocuments, user?.tenant_id]);

  const refreshPageData = useCallback(async () => {
    await refreshDocuments(true);
  }, [refreshDocuments]);

  refreshPageDataRef.current = refreshPageData;

  useEffect(() => {
    if (!configured) return;
    ensureCustomersLoaded();
  }, [configured, ensureCustomersLoaded]);

  useEffect(() => {
    pageCacheRef.current.clear();
    prefetchCacheRef.current.clear();
  }, [user?.tenant_id]);

  useEffect(() => {
    loadGenerationRef.current += 1;
    setPageOffset(0);
    setExpandedDocs(new Set());
    setExpandedCustomers(new Set());
    setCustomerDocuments({});
    setCustomerDocumentsLoading(new Set());
    setDocLineItems({});
    setDocLineItemsLoading(new Set());
    pageCacheRef.current.clear();
    prefetchCacheRef.current.clear();
  }, [
    categoryFilter,
    customerSearch,
    dateFrom,
    dateTo,
    lastInteractionFilter,
    lastInteractionFrom,
    lastInteractionTo,
    openOnly,
    viewMode,
    contactSort,
  ]);

  // Reload documents when server-side filters or pagination change (debounced for safety)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadDocuments();
    }, 150);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [loadDocuments]);

  useEffect(() => {
    if (viewMode === 'customer') {
      setSortField('open_amount');
      setSortDirection('desc');
      setExpandedCustomers(new Set());
      return;
    }
    setSortField('document_date');
    setSortDirection('desc');
  }, [viewMode]);

  useEffect(() => {
    const maybeRefreshDirtyData = () => {
      const dirtyAt = getRivhitDocumentsDirtyAt();
      if (!dirtyAt || dirtyAt === handledDirtyAtRef.current) return;
      handledDirtyAtRef.current = dirtyAt;
      refreshPageData().finally(() => {
        clearRivhitDocumentsDirty();
      });
    };

    maybeRefreshDirtyData();
    return subscribeToRivhitDocumentsDirty(maybeRefreshDirtyData);
  }, [refreshPageData]);

  const filterChips: { id: CategoryFilter; labelKey: string }[] = [
    { id: 'all', labelKey: 'documents.filterAll' },
    { id: 'invoice', labelKey: 'documents.filterInvoices' },
    { id: 'receipt', labelKey: 'documents.filterReceipts' },
    { id: 'invoice_receipt', labelKey: 'documents.filterInvoiceReceipts' },
    { id: 'delivery_note', labelKey: 'documents.filterDeliveryNotes' },
    { id: 'quote', labelKey: 'documents.filterQuotes' },
    { id: 'order', labelKey: 'documents.filterOrders' },
    { id: 'credit_invoice', labelKey: 'documents.filterCreditInvoices' },
    { id: 'return_note', labelKey: 'documents.filterReturnNotes' },
  ];

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount);

  const processedDocuments = useMemo(() => {
    return documents;
  }, [documents]);

  const sortedDocuments = useMemo(() => {
    if (contactSort) return processedDocuments;
    const items = [...processedDocuments];
    items.sort((a, b) => {
      let result = 0;
      switch (sortField) {
        case 'document_number':
          result = a.document_number - b.document_number;
          break;
        case 'customer_name':
          result = (a.customer_name || '').localeCompare(b.customer_name || '', 'he');
          break;
        case 'amount':
          result = a.amount - b.amount;
          break;
        case 'open_amount':
          result = (a.open_amount || 0) - (b.open_amount || 0);
          break;
        case 'invoices_count':
          result = (a.invoices_count || 0) - (b.invoices_count || 0);
          break;
        case 'document_date':
        default:
          result = parseDocumentDate(a.document_date) - parseDocumentDate(b.document_date);
          if (result === 0) {
            result = parseDocumentTime(a.document_time) - parseDocumentTime(b.document_time);
          }
          if (result === 0) {
            result = a.document_number - b.document_number;
          }
          break;
      }
      return sortDirection === 'asc' ? result : -result;
    });
    return items;
  }, [processedDocuments, sortDirection, sortField, contactSort]);

  const pageStart = totalCount === 0 ? 0 : pageOffset + 1;
  const pageEnd = Math.min(pageOffset + processedDocuments.length, totalCount);
  const showPagination = viewMode !== 'customer';

  const handleSort = (field: DocumentSortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection(field === 'document_date' ? 'desc' : 'asc');
  };

  const renderSortIndicator = (field: DocumentSortField) => {
    if (field !== sortField) return <span className="text-gray-300">↕</span>;
    return <span className="text-primary-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const isCustomerView = viewMode === 'customer';

  const toggleCustomerExpand = async (customerId: number) => {
    if (!customerId) return;

    // Check expand/collapse BEFORE the state update (React 18 may defer updaters)
    const isCurrentlyExpanded = expandedCustomers.has(customerId);

    if (isCurrentlyExpanded) {
      // Collapsing — just remove from set
      setExpandedCustomers(prev => {
        const next = new Set(prev);
        next.delete(customerId);
        return next;
      });
      return;
    }

    // Expanding
    setExpandedCustomers(prev => {
      const next = new Set(prev);
      next.add(customerId);
      return next;
    });

    const shouldLoad = !customerDocuments[customerId];
    console.log('[Documents] toggleCustomerExpand', customerId, '— shouldLoad:', shouldLoad, 'cachedDocs:', customerDocuments[customerId]?.length ?? 'none');
    if (!shouldLoad || customerDocumentsLoading.has(customerId)) return;

    setCustomerDocumentsLoading(prev => new Set(prev).add(customerId));
    try {
      const params = {
        ...buildParams(),
        customer_id: customerId,
        category_filter: 'invoice',
        group_by: undefined,
        offset: 0,
        limit: 500,
      };
      console.log('[Documents] Fetching customer docs, params:', JSON.stringify(params));
      const res = await listAllDocuments(params as Record<string, string | number | boolean | undefined>);
      console.log('[Documents] API response — total_count:', res.data.total_count, 'items:', res.data.items?.length ?? 0);
      const items = (res.data.items ?? []).map((item: UnifiedDocumentItem) => {
        const rememberedLink = getRememberedRivhitDocumentLinkForCategory(
          item.document_category,
          item.document_type,
          item.document_number,
        );
        return {
          ...item,
          document_link: item.document_link || rememberedLink,
        };
      });
      setCustomerDocuments(prev => ({ ...prev, [customerId]: items }));
    } catch (err) {
      console.error('[Documents] Failed to load customer documents:', err);
      setCustomerDocuments(prev => ({ ...prev, [customerId]: [] }));
    } finally {
      setCustomerDocumentsLoading(prev => {
        const next = new Set(prev);
        next.delete(customerId);
        return next;
      });
    }
  };

  const docExpandKey = (doc: UnifiedDocumentItem) => `${doc.document_category}-${doc.document_type}-${doc.document_number}`;

  const renderVatFooter = (key: string, spacerWidth: string = 'w-8') => {
    const vatInfo = docVatSummary[key];
    if (!vatInfo || (vatInfo.subtotal == null && vatInfo.total == null)) return null;
    return (
      <tfoot className="border-t border-gray-200 bg-gray-50/80">
        {vatInfo.subtotal != null && (
          <tr>
            <td className={spacerWidth + ' px-2 py-1'} />
            <td colSpan={3} className="px-4 py-1 text-end text-gray-500 text-xs">{t('documents.subtotal', 'Subtotal')}</td>
            <td className="px-4 py-1 text-end text-gray-600 text-xs">{formatAmount(vatInfo.subtotal)}</td>
          </tr>
        )}
        {vatInfo.vat_amount != null && (
          <tr>
            <td className={spacerWidth + ' px-2 py-1'} />
            <td colSpan={3} className="px-4 py-1 text-end text-gray-500 text-xs">
              {t('documents.vat', 'VAT')}{vatInfo.vat_rate != null ? ` (${vatInfo.vat_rate}%)` : ''}
            </td>
            <td className="px-4 py-1 text-end text-gray-600 text-xs">{formatAmount(vatInfo.vat_amount)}</td>
          </tr>
        )}
        {vatInfo.total != null && (
          <tr>
            <td className={spacerWidth + ' px-2 py-1.5'} />
            <td colSpan={3} className="px-4 py-1.5 text-end font-semibold text-gray-700 text-xs">{t('documents.totalWithVat', 'Total (incl. VAT)')}</td>
            <td className="px-4 py-1.5 text-end font-semibold text-gray-800 text-xs">{formatAmount(vatInfo.total)}</td>
          </tr>
        )}
      </tfoot>
    );
  };

  const toggleDocExpand = async (doc: UnifiedDocumentItem) => {
    const key = docExpandKey(doc);
    if (expandedDocs.has(key)) {
      setExpandedDocs(prev => { const next = new Set(prev); next.delete(key); return next; });
      return;
    }
    setExpandedDocs(prev => new Set(prev).add(key));
    if (docLineItems[key]) return; // already fetched

    setDocLineItemsLoading(prev => new Set(prev).add(key));
    try {
      const res = await getDocumentDetails(doc.document_type, doc.document_number);
      setDocLineItems(prev => ({ ...prev, [key]: res.data.items }));
      setDocVatSummary(prev => ({
        ...prev,
        [key]: {
          subtotal: res.data.subtotal,
          vat_rate: res.data.vat_rate,
          vat_amount: res.data.vat_amount,
          total: res.data.total,
        },
      }));
    } catch (err) {
      console.error('[Documents] Failed to load document details:', err);
      setDocLineItems(prev => ({ ...prev, [key]: [] }));
    } finally {
      setDocLineItemsLoading(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const handleSendDocumentWhatsApp = (doc: UnifiedDocumentItem) => {
    if (!doc.customer_id || !doc.document_link) return;
    setWhatsAppDocument(doc);
  };

  const handleViewDocument = (documentLink?: string) => {
    if (!documentLink) return;
    window.open(documentLink, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadDocument = (documentLink?: string) => {
    if (!documentLink) return;
    const link = window.document.createElement('a');
    link.href = documentLink;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = '';
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
  };

  const handleOpenClosingLink = async (closingLink: ClosingLink) => {
    try {
      const resp = closingLink.document_category === 'receipt'
        ? await getReceiptPdfLink(closingLink.document_type, closingLink.document_number)
        : await getDocumentPdfLink(closingLink.document_type, closingLink.document_number);
      const pdfLink = resp.data?.document_link;
      if (pdfLink) {
        window.open(pdfLink, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Failed to fetch PDF link for closing document:', err);
    }
  };

  const handleCustomerClick = (doc: UnifiedDocumentItem) => {
    if (!doc.customer_id) return;
    const customer = customers.find((candidate) => candidate.customer_id === doc.customer_id);
    setSelectedCustomer(customer || { customer_id: doc.customer_id, last_name: doc.customer_name, first_name: '' });
  };

  const findCustomerPhone = (customerId: number) =>
    customers.find((candidate) => candidate.customer_id === customerId)?.phone || null;

  const refreshCustomerHistory = () => {
    pageCacheRef.current.clear();
    prefetchCacheRef.current.clear();
    clearTenantDocumentsLocalCache(user?.tenant_id);
    void loadDocuments(false);
  };

  const renderLastInteraction = (doc: UnifiedDocumentItem) => {
    const activity = doc.last_service_activity;
    const serviceActivity = activity && (
      <div className="mt-2 border-t border-gray-100 pt-2 text-xs">
        <div className="font-medium text-gray-700">{t('customerWork.latestActivity')}: {t(`customerWork.types.${activity.activity_type}`)}</div>
        <div className="text-gray-500">{workDate(activity.occurred_at, i18n.language)} · {activity.author_name}</div>
        <div className="max-w-64 whitespace-pre-wrap text-gray-600">{activity.notes}</div>
      </div>
    );
    if (!doc.last_collection_contact_at) {
      return <div><span className="text-xs text-gray-400">{t('collectionWorkflow.noContact')}</span><ReconciliationWarning data={doc.reconciliation} compact />{serviceActivity}</div>;
    }
    const timestamp = new Date(doc.last_collection_contact_at);
    const formattedTimestamp = Number.isNaN(timestamp.getTime())
      ? doc.last_collection_contact_at
      : new Intl.DateTimeFormat(i18n.language === 'he' ? 'he-IL' : 'en-GB', {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(timestamp);
    return (
      <div className="min-w-36">
        <ReconciliationWarning data={doc.reconciliation} compact />
        <div className="text-xs font-medium text-gray-700">{doc.last_collection_contact_channel}</div>
        {contactedThisMonth(doc.last_collection_contact_at, calendarNow) && <span className="rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-800">{t('collectionWorkflow.contactedMonth')}</span>}
        <div className="mt-0.5 text-xs text-gray-400">{formattedTimestamp}</div>
        {serviceActivity}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('documents.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('documents.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshPageData}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={t('common.refresh', 'Refresh')}
          >
            <span className={loading ? 'animate-spin' : ''}>🔄</span>
            {t('common.refresh', 'Refresh')}
          </button>
          <button
            onClick={() => navigate('/create-document')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
          >
            {t('documents.newDocument')}
          </button>
        </div>
      </div>

      {/* Sync status banner */}
      <RivhitSyncBanner syncState={syncState} />

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{t('documents.filtersTitle', 'Filters')}</h3>

        {/* Row 1: Category chips + view toggles */}
        <div className="flex flex-wrap items-center gap-2">
          {filterChips.map(chip => (
            <button
              key={chip.id}
              data-testid={`doc-filter-${chip.id}`}
              aria-pressed={categoryFilter === chip.id}
              onClick={() => setCategoryFilter(chip.id)}
              className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors ${
                categoryFilter === chip.id
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'
              }`}
            >
              {t(chip.labelKey)}
            </button>
          ))}
          <div className="w-px h-5 bg-gray-200 mx-1" />
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={viewMode === 'customer'}
              onChange={() => setViewMode(viewMode === 'customer' ? 'documents' : 'customer')}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-700">{t('documents.viewByCustomer', 'By customer')}</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={openOnly}
              onChange={() => setOpenOnly((prev) => !prev)}
              className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
            />
            <span className="text-sm text-gray-700">{t('documents.openInvoicesOnly', 'Open invoices only')}</span>
          </label>
        </div>

        {/* Row 2: Date inputs + quick ranges */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span className="whitespace-nowrap">{t('documents.dateFrom')}</span>
            <input
              type="date"
              value={dateFrom}
              onChange={e => handleDateFromChange(e.target.value)}
              className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span className="whitespace-nowrap">{t('documents.dateTo')}</span>
            <input
              type="date"
              value={dateTo}
              onChange={e => handleDateToChange(e.target.value)}
              className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </label>
          <div className="w-px h-5 bg-gray-200" />
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500">{t('documents.quickRange', 'Quick range')}:</span>
            {([
              { key: 'today', label: t('documents.rangeToday', 'Today'), from: getTodayIsoDate(), to: getTodayIsoDate() },
              { key: 'yesterday', label: t('documents.rangeYesterday', 'Yesterday'), from: getDaysAgoIsoDate(1), to: getDaysAgoIsoDate(1) },
              { key: 'week', label: t('documents.rangeThisWeek', 'This week'), from: getStartOfWeek(), to: getTodayIsoDate() },
              { key: 'month', label: t('documents.rangeThisMonth', 'This month'), from: getStartOfMonth(), to: getTodayIsoDate() },
              { key: 'last30', label: t('documents.rangeLast30Days', 'Last 30 days'), from: getDaysAgoIsoDate(29), to: getTodayIsoDate() },
              { key: 'prevMonth', label: t('documents.rangePreviousMonth', 'Previous month'), from: getStartOfPreviousMonth(), to: getEndOfPreviousMonth() },
              { key: 'last3months', label: t('documents.rangeLast3Months', 'Last 3 months'), from: getMonthsAgoIsoDate(3), to: getTodayIsoDate() },
              { key: 'last6months', label: t('documents.rangeLast6Months', 'Last 6 months'), from: getMonthsAgoIsoDate(6), to: getTodayIsoDate() },
              { key: 'startOfYear', label: t('documents.rangeStartOfYear', 'Start of year'), from: getStartOfYearIsoDate(), to: getTodayIsoDate() },
              { key: 'last1year', label: t('documents.rangeLast1Year', 'Last year'), from: getMonthsAgoIsoDate(12), to: getTodayIsoDate() },
            ] as const).map(range => {
              const isActive = activeRangeKey === range.key;
              return (
                <button
                  key={range.key}
                  type="button"
                  onClick={() => { setDateFrom(range.from); setDateTo(range.to); setActiveRangeKey(range.key); }}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                    isActive
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'
                  }`}
                >
                  {range.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 3: Last interaction filter */}
        <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
          <span className="text-sm font-medium text-gray-700">
            {t('collectionWorkflow.lastContact')}
          </span>
          <select
            value={lastInteractionFilter}
            onChange={(event) => setLastInteractionFilter(event.target.value as LastInteractionFilter)}
            aria-label={t('collectionWorkflow.lastContact')}
            className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          >
            <option value="all">{t('documents.lastInteractionAll', 'All')}</option>
            <option value="range">{t('documents.lastInteractionDateRange', 'Date range')}</option>
            <option value="none">{t('collectionWorkflow.noContact')}</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span className="whitespace-nowrap">{t('collectionWorkflow.contactFrom')}</span>
            <input
              type="date"
              value={lastInteractionFrom}
              onChange={(event) => handleLastInteractionFromChange(event.target.value)}
              disabled={lastInteractionFilter !== 'range'}
              data-testid="last-interaction-from"
              className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </label>
          <select aria-label={t('collectionWorkflow.contactSort')} value={contactSort}
            onChange={event => { setContactSort(event.target.value as '' | 'asc' | 'desc'); setPageOffset(0); }}
            className="rounded-md border px-3 py-1.5 text-sm">
            <option value="">{t('collectionWorkflow.defaultSort')}</option>
            <option value="desc">{t('collectionWorkflow.newest')}</option>
            <option value="asc">{t('collectionWorkflow.oldest')}</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <span className="whitespace-nowrap">{t('collectionWorkflow.contactTo')}</span>
            <input
              type="date"
              value={lastInteractionTo}
              onChange={(event) => handleLastInteractionToChange(event.target.value)}
              disabled={lastInteractionFilter !== 'range'}
              data-testid="last-interaction-to"
              className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </label>
        </div>

        {/* Row 4: Document search */}
        <input
          type="text"
          value={customerSearch}
          onChange={e => setCustomerSearch(e.target.value)}
          placeholder={t('documents.searchAllAttributes')}
          className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
        />
      </div>

      {!serviceError && summary.uncertain_open_invoices_count > 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert" data-testid="documents-balance-warning">
          {t(
            'documents.uncertainBalanceWarning',
            '{{count}} invoice balance could not be verified and is excluded from open-debt totals.',
            { count: summary.uncertain_open_invoices_count },
          )}
          {summary.uncertain_document_numbers.length > 0 && (
            <span className="ms-1 font-medium">
              #{summary.uncertain_document_numbers.join(', #')}
            </span>
          )}
        </div>
      )}

      {/* Summary KPI cards */}
      {!serviceError && <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('documents.summaryInvoicesTotal')}</div>
          {summaryLoading
            ? <div className="mt-1 h-8 w-28 animate-pulse rounded-md bg-gray-200" />
            : <div className="mt-1 text-2xl font-bold text-gray-900">{formatAmount(summary.total_invoices_amount)}</div>}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('documents.summaryPaid')}</div>
          {summaryLoading
            ? <div className="mt-1 h-8 w-28 animate-pulse rounded-md bg-gray-200" />
            : <div className="mt-1 text-2xl font-bold text-green-700">{formatAmount(summary.total_receipts_amount)}</div>}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('documents.summaryInvoiceReceipts')}</div>
          {summaryLoading
            ? <div className="mt-1 h-8 w-28 animate-pulse rounded-md bg-gray-200" />
            : <div className="mt-1 text-2xl font-bold text-purple-700">{formatAmount(summary.total_invoice_receipts_amount)}</div>}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('documents.summaryOpenInvoices')}</div>
          {summaryLoading
            ? <div className="mt-1 h-8 w-28 animate-pulse rounded-md bg-gray-200" />
            : <div className="mt-1 text-2xl font-bold text-red-600">{summary.total_open_invoices_count}</div>}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('documents.summaryOpen')}</div>
          {summaryLoading
            ? <div className="mt-1 h-8 w-28 animate-pulse rounded-md bg-gray-200" />
            : <div className="mt-1 text-2xl font-bold text-amber-700">{formatAmount(summary.total_open_amount)}</div>}
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('documents.summaryDeliveryNotes')}</div>
          {summaryLoading
            ? <div className="mt-1 h-8 w-28 animate-pulse rounded-md bg-gray-200" />
            : (
              <>
                <div className="mt-1 text-2xl font-bold text-orange-700">{summary.total_delivery_notes_count}</div>
                <div className="mt-1 text-xs text-gray-500">
                  <span className="font-medium text-amber-700">{summary.total_open_delivery_notes_count}</span>
                  {' '}{t('documents.deliveryNotesOpenSuffix')}
                  {' · '}
                  <span className="font-medium text-green-700">{summary.total_closed_delivery_notes_count}</span>
                  {' '}{t('documents.deliveryNotesInvoicedSuffix')}
                </div>
              </>
            )}
        </div>
      </div>}

      {/* Documents table */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            {t('documents.loading')}
          </div>
        ) : serviceError ? (
          <div className="p-8 text-center text-sm text-red-700" role="alert">
            <p>{serviceError}</p>
            <button type="button" onClick={() => void loadDocuments(false)} className="mt-3 rounded-md border border-red-200 px-3 py-2 hover:bg-red-50">
              {t('common.retry', 'Retry')}
            </button>
          </div>
        ) : sortedDocuments.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">
            {t('documents.noDocuments')}
          </div>
        ) : (
          <>
            {/* Count */}
            <div className="flex flex-col gap-2 border-b border-gray-100 px-4 py-2 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
              <span>
                {pageStart}-{pageEnd} {t('common.of', 'of')} {totalCount} {isCustomerView ? t('documents.customersCountLabel', 'customers') : t('documents.title').toLowerCase()}
              </span>
              {showPagination && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPageOffset((prev) => Math.max(prev - DOCUMENTS_PAGE_SIZE, 0))}
                  disabled={loading || pageOffset === 0}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                >
                  {t('common.previous', 'Previous')}
                </button>
                <button
                  type="button"
                  onClick={() => setPageOffset((prev) => prev + DOCUMENTS_PAGE_SIZE)}
                  disabled={loading || !hasMore}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                >
                  {t('common.next', 'Next')}
                </button>
              </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
                    <th className="w-8 px-2 py-3" />
                    <th className="px-4 py-3 text-start font-medium">
                      <button type="button" onClick={() => handleSort('customer_name')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                        <span>{t('documents.customer')}</span>
                        {renderSortIndicator('customer_name')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-start font-medium">
                      {t('collectionWorkflow.lastContact')}
                    </th>
                    {isCustomerView ? (
                      <>
                        <th className="px-4 py-3 text-end font-medium">
                          <button type="button" onClick={() => handleSort('invoices_count')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.invoicesCount', 'Invoices')}</span>
                            {renderSortIndicator('invoices_count')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-end font-medium">
                          <button type="button" onClick={() => handleSort('amount')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.invoicesAmount', 'Invoices amount')}</span>
                            {renderSortIndicator('amount')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-end font-medium">{t('documents.paidAmount', 'Paid')}</th>
                        <th className="px-4 py-3 text-end font-medium">
                          <button type="button" onClick={() => handleSort('open_amount')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.openAmount', 'Open')}</span>
                            {renderSortIndicator('open_amount')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-end font-medium">{t('documents.actions', 'Actions')}</th>
                      </>
                    ) : (
                      <>
                        <th className="px-4 py-3 text-start font-medium">{t('documents.type')}</th>
                        <th className="px-4 py-3 text-start font-medium">
                          <button type="button" onClick={() => handleSort('document_number')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.docNumber')}</span>
                            {renderSortIndicator('document_number')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-start font-medium">
                          <button type="button" onClick={() => handleSort('document_date')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.date')}</span>
                            {renderSortIndicator('document_date')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-end font-medium">
                          <button type="button" onClick={() => handleSort('amount')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
                            <span>{t('documents.amount')}</span>
                            {renderSortIndicator('amount')}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-end font-medium">{t('documents.openAmount', 'Open')}</th>
                        <th className="px-4 py-3 text-end font-medium">{t('common.actions', 'Actions')}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sortedDocuments.map((doc) => {
                    const badge = CATEGORY_BADGES[doc.document_category] || CATEGORY_BADGES.invoice;
                    const isExpanded = isCustomerView && expandedCustomers.has(doc.customer_id || 0);
                    const customerId = doc.customer_id || 0;
                    const childDocs = customerDocuments[customerId] || doc.documents || [];
                    const isCustomerDocsLoading = customerDocumentsLoading.has(customerId);
                    if (isExpanded) {
                      console.log('[Documents] Rendering expanded customer', customerId, doc.customer_name, '— childDocs:', childDocs.length, 'loading:', isCustomerDocsLoading, 'fromCache:', !!customerDocuments[customerId], 'fromInline:', !!(doc.documents?.length));
                    }
                    const customerRowKey = [
                      'customer',
                      doc.customer_id ?? 'unknown',
                      doc.customer_name || 'unnamed',
                      doc.invoices_count || 0,
                      doc.amount || 0,
                      doc.open_amount || 0,
                    ].join('-');
                    const customerChildrenKey = `${customerRowKey}-children`;
                    const documentRowKey = `${doc.document_category}-${doc.document_type}-${doc.document_number}-${doc.document_date}-${doc.document_time || 'na'}`;
                    return isCustomerView ? (
                      <React.Fragment key={customerRowKey}>{/* Customer aggregate row */}
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td className="px-2 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => toggleCustomerExpand(customerId)}
                              className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 transition-colors text-gray-500"
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              <span className={`inline-block transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                            </button>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {doc.customer_id ? (
                              <button
                                type="button"
                                onClick={() => handleCustomerClick(doc)}
                                className="text-start text-primary-700 underline-offset-2 transition-colors hover:text-primary-800 hover:underline disabled:cursor-default disabled:text-gray-700 disabled:no-underline font-semibold"
                              >
                                {doc.customer_name || `#${doc.customer_id}`}
                              </button>
                            ) : (
                              <span className="font-semibold">{doc.customer_name || '—'}</span>
                            )}
                          </td>
                          <td className="px-4 py-3">{renderLastInteraction(doc)}</td>
                          <td className="px-4 py-3 text-end text-gray-700">{doc.invoices_count || 0}</td>
                          <td className="px-4 py-3 text-end font-medium text-gray-900">{formatAmount(doc.amount)}</td>
                          <td className="px-4 py-3 text-end text-green-700">{formatAmount(doc.paid_amount || 0)}</td>
                          <td className="px-4 py-3 text-end font-semibold text-amber-700">
                            {doc.balance_status === 'unknown'
                              ? t('documents.openAmountUnavailable', 'Unavailable')
                              : formatAmount(doc.open_amount || 0)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setWhatsAppCustomer({
                                    customerId,
                                    displayName: doc.customer_name,
                                    phone: findCustomerPhone(customerId),
                                    openAmount: doc.open_amount || 0,
                                  });
                                }}
                                disabled={!customerId}
                                className="inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                title={t('documents.sendWhatsApp')}
                              >
                                <span>💬</span>
                                {t('documents.sendWhatsApp')}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  printCustomerReport({
                                    customerId,
                                    customerName: doc.customer_name || `#${customerId}`,
                                    invoicesAmount: doc.amount,
                                    paidAmount: doc.paid_amount || 0,
                                    openAmount: doc.open_amount || 0,
                                    t,
                                    i18n,
                                  });
                                }}
                                disabled={!customerId}
                                className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                title={t('documents.printReport')}
                              >
                                <span>🖨️</span>
                                {t('documents.printReport')}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {/* Expanded child document rows */}
                        {isExpanded && (
                          <tr key={customerChildrenKey}>
                            <td colSpan={8} className="p-0">
                              {isCustomerDocsLoading ? (
                                <div className="px-12 py-3 text-sm text-gray-400 bg-gray-50/50">{t('documents.loading')}</div>
                              ) : childDocs.length > 0 ? (
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-gray-100/60 text-gray-500 text-xs uppercase tracking-wider">
                                      <th className="w-8 px-2 py-2" />
                                      <th className="px-4 py-2 text-start font-medium">{t('documents.docNumber')}</th>
                                      <th className="px-4 py-2 text-start font-medium">{t('documents.date')}</th>
                                      <th className="px-4 py-2 text-end font-medium">{t('documents.amount')}</th>
                                      <th className="px-4 py-2 text-end font-medium">{t('documents.openAmount', 'Open')}</th>
                                      <th className="px-4 py-2 text-end font-medium">{t('common.actions', 'Actions')}</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-50">
                                    {childDocs.map((child) => {
                                    const childKey = docExpandKey(child);
                                    const isChildExpanded = expandedDocs.has(childKey);
                                    const childLineItems = docLineItems[childKey];
                                    const isChildLoading = docLineItemsLoading.has(childKey);
                                    return (
                                      <React.Fragment key={`child-${child.document_category}-${childKey}-${child.document_date}-${child.document_time || 'na'}`}>
                                        <tr className="bg-gray-50/50 hover:bg-gray-100/50 transition-colors cursor-pointer" onClick={() => toggleDocExpand(child)}>
                                          <td className="w-8 px-2 py-2 text-center">
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); toggleDocExpand(child); }}
                                              className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-gray-200 transition-colors text-gray-400"
                                              aria-label={isChildExpanded ? 'Collapse' : 'Expand'}
                                            >
                                              {isChildLoading ? (
                                                <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
                                              ) : (
                                                <span className={`inline-block text-xs transition-transform ${isChildExpanded ? 'rotate-90' : ''}`}>▶</span>
                                              )}
                                            </button>
                                          </td>
                                          <td className="px-4 py-2 text-gray-900 font-medium">{child.document_number}</td>
                                          <td className="px-4 py-2 text-gray-600">
                                            <div>{child.document_date}</div>
                                            {child.document_time && (
                                              <div className="text-xs text-gray-400">{child.document_time}</div>
                                            )}
                                          </td>
                                          <td className="px-4 py-2 text-end font-medium text-gray-900">{formatAmount(child.amount)}</td>
                                          <td className="px-4 py-2 text-end font-semibold text-amber-700">
                                            {child.balance_status === 'unknown'
                                              ? t('documents.openAmountUnavailable', 'Unavailable')
                                              : formatAmount(child.open_amount || 0)}
                                          </td>
                                          <td className="px-4 py-2">
                                            <div className="flex items-center justify-end gap-2">
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleSendDocumentWhatsApp({
                                                    ...child,
                                                    customer_id: child.customer_id ?? doc.customer_id,
                                                    customer_name: child.customer_name || doc.customer_name,
                                                  });
                                                }}
                                                disabled={!(child.customer_id ?? doc.customer_id) || !child.document_link}
                                                className="inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                                title={t('documents.sendWhatsApp')}
                                              >
                                                <span>💬</span>
                                                {t('documents.sendWhatsApp')}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handleViewDocument(child.document_link); }}
                                                disabled={!child.document_link}
                                                className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                              >
                                                {t('documents.viewDocument', 'View')}
                                              </button>
                                              <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); handleDownloadDocument(child.document_link); }}
                                                disabled={!child.document_link}
                                                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                              >
                                                {t('documents.downloadDocument', 'Download')}
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                        {isChildExpanded && (
                                          <tr>
                                            <td colSpan={6} className="p-0">
                                              {isChildLoading ? (
                                                <div className="px-12 py-2 text-xs text-gray-400 bg-gray-100/40">{t('documents.loadingItems', 'Loading items...')}</div>
                                              ) : childLineItems && childLineItems.length > 0 ? (
                                                <table className="w-full text-xs">
                                                  <thead>
                                                    <tr className="bg-gray-100/40 text-gray-400 uppercase tracking-wider">
                                                      <th className="w-12 px-2 py-1.5" />
                                                      <th className="px-4 py-1.5 text-start font-medium">{t('documents.itemDescription', 'Description')}</th>
                                                      <th className="px-4 py-1.5 text-end font-medium">{t('documents.itemQuantity', 'Qty')}</th>
                                                      <th className="px-4 py-1.5 text-end font-medium">{t('documents.itemPrice', 'Price')}</th>
                                                      <th className="px-4 py-1.5 text-end font-medium">{t('documents.itemTotal', 'Total')}</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="divide-y divide-gray-50">
                                                    {childLineItems.map((item, iIdx) => (
                                                      <tr key={`cline-${iIdx}`} className="bg-white/60 hover:bg-gray-50/80 transition-colors">
                                                        <td className="w-12 px-2 py-1.5" />
                                                        <td className="px-4 py-1.5 text-gray-800">{item.description || '—'}</td>
                                                        <td className="px-4 py-1.5 text-end text-gray-600">{item.quantity}</td>
                                                        <td className="px-4 py-1.5 text-end text-gray-600">{item.price_nis != null ? formatAmount(item.price_nis) : '—'}</td>
                                                        <td className="px-4 py-1.5 text-end font-medium text-gray-800">{item.total_nis != null ? formatAmount(item.total_nis) : '—'}</td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                  {renderVatFooter(childKey, 'w-12')}
                                                </table>
                                              ) : (
                                                <div className="px-12 py-2 text-xs text-gray-400 bg-gray-100/40">{t('documents.noItems', 'No line items')}</div>
                                              )}
                                              {/* Closing links in grouped child docs */}
                                              {child.closed_by && child.closed_by.length > 0 && (
                                                <div className="border-t border-gray-200 bg-blue-50/40 px-10 py-2">
                                                  <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-1">
                                                    {t('documents.closedByTitle', 'Closed by')}
                                                  </div>
                                                  {child.closed_by.map((link: { document_category?: string; document_type?: number; document_number: number; amount: number; close_status: string }, lIdx: number) => (
                                                    <div key={`ccb-${lIdx}`} className="flex items-center gap-3 text-xs py-0.5">
                                                      <button
                                                        type="button"
                                                        onClick={() => handleOpenClosingLink({ document_category: link.document_category || 'receipt', document_type: link.document_type || 1, document_number: link.document_number, amount: link.amount, close_status: link.close_status })}
                                                        className="text-blue-800 font-medium hover:text-blue-600 hover:underline cursor-pointer"
                                                        title={t('documents.viewClosingReceipt', 'View receipt PDF')}
                                                      >
                                                        {t('documents.closingReceipt', 'Receipt')} #{link.document_number} 📄
                                                      </button>
                                                      <span className="text-gray-700">{formatAmount(link.amount)}</span>
                                                      <span className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium ${link.close_status === 'fully_closed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                        {link.close_status === 'fully_closed' ? t('documents.closingFullyClosed') : t('documents.closingPartiallyClosed')}
                                                      </span>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                              {child.closing_documents && child.closing_documents.length > 0 && (
                                                <div className="border-t border-gray-200 bg-emerald-50/40 px-10 py-2">
                                                  <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-1">
                                                    {t('documents.closingDocumentsTitle', 'Closing invoices')}
                                                  </div>
                                                  {child.closing_documents.map((link: { document_category?: string; document_type?: number; document_number: number; amount: number; close_status: string }, lIdx: number) => (
                                                    <div key={`ccd-${lIdx}`} className="flex items-center gap-3 text-xs py-0.5">
                                                      <button
                                                        type="button"
                                                        onClick={() => handleOpenClosingLink({ document_category: link.document_category || 'invoice', document_type: link.document_type || 1, document_number: link.document_number, amount: link.amount, close_status: link.close_status })}
                                                        className="text-emerald-800 font-medium hover:text-emerald-600 hover:underline cursor-pointer"
                                                        title={t('documents.viewClosingInvoice', 'View invoice PDF')}
                                                      >
                                                        {t('documents.closingInvoice', 'Invoice')} #{link.document_number} 📄
                                                      </button>
                                                      <span className="text-gray-700">{formatAmount(link.amount)}</span>
                                                      <span className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium ${link.close_status === 'fully_closed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                        {link.close_status === 'fully_closed' ? t('documents.closingFullyClosed') : t('documents.closingPartiallyClosed')}
                                                      </span>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                    })}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="px-12 py-3 text-sm text-gray-400 bg-gray-50/50">{t('documents.noDocuments')}</div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ) : (
                      <React.Fragment key={documentRowKey}>{/* Document row with expand */}
                        {(() => {
                          const dKey = docExpandKey(doc);
                          const isDocExpanded = expandedDocs.has(dKey);
                          const lineItems = docLineItems[dKey];
                          const isLineItemsLoading = docLineItemsLoading.has(dKey);
                          return (
                            <>
                              <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => toggleDocExpand(doc)}>
                                <td className="px-2 py-3 text-center">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); toggleDocExpand(doc); }}
                                    className="inline-flex items-center justify-center w-6 h-6 rounded hover:bg-gray-200 transition-colors text-gray-500"
                                    aria-label={isDocExpanded ? 'Collapse' : 'Expand'}
                                  >
                                    {isLineItemsLoading ? (
                                      <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
                                    ) : (
                                      <span className={`inline-block transition-transform ${isDocExpanded ? 'rotate-90' : ''}`}>▶</span>
                                    )}
                                  </button>
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  {doc.customer_id ? (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleCustomerClick(doc); }}
                                      className="text-start text-primary-700 underline-offset-2 transition-colors hover:text-primary-800 hover:underline disabled:cursor-default disabled:text-gray-700 disabled:no-underline"
                                    >
                                      {doc.customer_name || `#${doc.customer_id}`}
                                    </button>
                                  ) : (
                                    doc.customer_name || '—'
                                  )}
                                </td>
                                <td className="px-4 py-3">{renderLastInteraction(doc)}</td>
                                <td className="px-4 py-3">
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.bg} ${badge.text}`}>
                                      {t(badge.labelKey)}
                                    </span>
                                    {doc.document_category === 'delivery_note' && (
                                      doc.is_cancelled ? (
                                        <span
                                          className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-600"
                                          title={t('documents.deliveryNoteCancelledHint')}
                                        >
                                          {t('documents.deliveryNoteCancelled')}
                                        </span>
                                      ) : (
                                        <span
                                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${doc.is_closed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}
                                          title={doc.is_closed ? t('documents.deliveryNoteInvoicedHint') : t('documents.deliveryNoteOpenHint')}
                                        >
                                          {doc.is_closed ? t('documents.deliveryNoteInvoiced') : t('documents.deliveryNoteOpen')}
                                        </span>
                                      )
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-gray-900 font-medium">
                                  {doc.document_number}
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                  <div>{doc.document_date}</div>
                                  {doc.document_time && (
                                    <div className="text-xs text-gray-400">{doc.document_time}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-end font-medium text-gray-900">
                                  {formatAmount(doc.amount)}
                                </td>
                                <td className="px-4 py-3 text-end font-semibold text-amber-700">
                                  {doc.document_category === 'invoice'
                                    ? doc.balance_status === 'unknown'
                                      ? t('documents.openAmountUnavailable', 'Unavailable')
                                      : formatAmount(doc.open_amount || 0)
                                    : '—'}
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center justify-end gap-2">
                                    <span
                                      className={!doc.customer_id || !doc.document_link ? 'cursor-not-allowed' : ''}
                                      title={!doc.document_link ? t('documents.noLinkHint') : !doc.customer_id ? t('documents.noCustomerHint') : t('documents.sendWhatsApp')}
                                    >
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleSendDocumentWhatsApp(doc);
                                        }}
                                        disabled={!doc.customer_id || !doc.document_link}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:pointer-events-none"
                                      >
                                        <span>💬</span>
                                        {t('documents.sendWhatsApp')}
                                      </button>
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleViewDocument(doc.document_link); }}
                                      disabled={!doc.document_link}
                                      className="inline-flex items-center rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                      {t('documents.viewDocument', 'View')}
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleDownloadDocument(doc.document_link); }}
                                      disabled={!doc.document_link}
                                      className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                                    >
                                      {t('documents.downloadDocument', 'Download')}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                              {isDocExpanded && (
                                <tr key={`items-${documentRowKey}`}>
                                  <td colSpan={9} className="p-0">
                                    {isLineItemsLoading ? (
                                      <div className="px-8 py-3 text-sm text-gray-400 bg-gray-50/50">{t('documents.loadingItems', 'Loading items...')}</div>
                                    ) : lineItems && lineItems.length > 0 ? (
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="bg-gray-100/60 text-gray-500 text-xs uppercase tracking-wider">
                                            <th className="w-8 px-2 py-2" />
                                            <th className="px-4 py-2 text-start font-medium">{t('documents.itemDescription', 'Description')}</th>
                                            <th className="px-4 py-2 text-end font-medium">{t('documents.itemQuantity', 'Qty')}</th>
                                            <th className="px-4 py-2 text-end font-medium">{t('documents.itemPrice', 'Price')}</th>
                                            <th className="px-4 py-2 text-end font-medium">{t('documents.itemTotal', 'Total')}</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                          {lineItems.map((item, iIdx) => (
                                            <tr key={`line-${iIdx}`} className="bg-gray-50/50 hover:bg-gray-100/50 transition-colors">
                                              <td className="w-8 px-2 py-2" />
                                              <td className="px-4 py-2 text-gray-900">{item.description || '—'}</td>
                                              <td className="px-4 py-2 text-end text-gray-700">{item.quantity}</td>
                                              <td className="px-4 py-2 text-end text-gray-700">{item.price_nis != null ? formatAmount(item.price_nis) : '—'}</td>
                                              <td className="px-4 py-2 text-end font-medium text-gray-900">{item.total_nis != null ? formatAmount(item.total_nis) : '—'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                        {renderVatFooter(dKey)}
                                      </table>
                                    ) : (
                                      <div className="px-8 py-3 text-sm text-gray-400 bg-gray-50/50">{t('documents.noItems', 'No line items')}</div>
                                    )}
                                    {/* Closing links: closed_by (on invoices) */}
                                    {doc.closed_by && doc.closed_by.length > 0 && (
                                      <div className="border-t border-gray-200 bg-blue-50/50 px-8 py-3">
                                        <div className="text-xs font-semibold text-blue-700 uppercase tracking-wider mb-2">
                                          {t('documents.closedByTitle', 'Closed by')}
                                        </div>
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="text-gray-500 text-xs uppercase tracking-wider">
                                              <th className="px-3 py-1 text-start font-medium">{t('documents.closingReceipt', 'Receipt')}</th>
                                              <th className="px-3 py-1 text-end font-medium">{t('documents.closingAmount', 'Amount')}</th>
                                              <th className="px-3 py-1 text-start font-medium">{t('documents.closingStatus', 'Status')}</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {doc.closed_by.map((link: { document_category?: string; document_type: number; document_number: number; amount: number; close_status: string }, lIdx: number) => (
                                              <tr key={`cb-${lIdx}`}>
                                                <td className="px-3 py-1">
                                                  <button
                                                    type="button"
                                                    onClick={() => handleOpenClosingLink({ ...link, document_category: link.document_category || 'receipt' })}
                                                    className="text-blue-800 font-medium hover:text-blue-600 hover:underline cursor-pointer"
                                                    title={t('documents.viewClosingReceipt', 'View receipt PDF')}
                                                  >
                                                    #{link.document_number} 📄
                                                  </button>
                                                </td>
                                                <td className="px-3 py-1 text-end text-gray-900">{formatAmount(link.amount)}</td>
                                                <td className="px-3 py-1">
                                                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${link.close_status === 'fully_closed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                    {link.close_status === 'fully_closed' ? t('documents.closingFullyClosed', 'Fully closed') : t('documents.closingPartiallyClosed', 'Partially closed')}
                                                  </span>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                    {/* Closing links: closing_documents (on receipts) */}
                                    {doc.closing_documents && doc.closing_documents.length > 0 && (
                                      <div className="border-t border-gray-200 bg-emerald-50/50 px-8 py-3">
                                        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider mb-2">
                                          {t('documents.closingDocumentsTitle', 'Closing invoices')}
                                        </div>
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="text-gray-500 text-xs uppercase tracking-wider">
                                              <th className="px-3 py-1 text-start font-medium">{t('documents.closingInvoice', 'Invoice')}</th>
                                              <th className="px-3 py-1 text-end font-medium">{t('documents.closingAmount', 'Amount')}</th>
                                              <th className="px-3 py-1 text-start font-medium">{t('documents.closingStatus', 'Status')}</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {doc.closing_documents.map((link: { document_category?: string; document_type: number; document_number: number; amount: number; close_status: string }, lIdx: number) => (
                                              <tr key={`cd-${lIdx}`}>
                                                <td className="px-3 py-1">
                                                  <button
                                                    type="button"
                                                    onClick={() => handleOpenClosingLink({ ...link, document_category: link.document_category || 'invoice' })}
                                                    className="text-emerald-800 font-medium hover:text-emerald-600 hover:underline cursor-pointer"
                                                    title={t('documents.viewClosingInvoice', 'View invoice PDF')}
                                                  >
                                                    #{link.document_number} 📄
                                                  </button>
                                                </td>
                                                <td className="px-3 py-1 text-end text-gray-900">{formatAmount(link.amount)}</td>
                                                <td className="px-3 py-1">
                                                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${link.close_status === 'fully_closed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                                    {link.close_status === 'fully_closed' ? t('documents.closingFullyClosed', 'Fully closed') : t('documents.closingPartiallyClosed', 'Partially closed')}
                                                  </span>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Bottom pagination */}
            <div className="flex flex-col gap-2 border-t border-gray-100 px-4 py-2 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
              <span>
                {pageStart}-{pageEnd} {t('common.of', 'of')} {totalCount} {isCustomerView ? t('documents.customersCountLabel', 'customers') : t('documents.title').toLowerCase()}
              </span>
              {showPagination && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPageOffset((prev) => Math.max(prev - DOCUMENTS_PAGE_SIZE, 0))}
                  disabled={loading || pageOffset === 0}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                >
                  {t('common.previous', 'Previous')}
                </button>
                <button
                  type="button"
                  onClick={() => setPageOffset((prev) => prev + DOCUMENTS_PAGE_SIZE)}
                  disabled={loading || !hasMore}
                  className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                >
                  {t('common.next', 'Next')}
                </button>
              </div>
              )}
            </div>

          </>
        )}
      </div>

      {selectedCustomer && (
        <CustomerDetailsModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onCollectionContact={refreshCustomerHistory}
          onActivitySaved={refreshCustomerHistory}
        />
      )}

      {whatsAppCustomer && (
        <DebtReportSendModal
          customerId={whatsAppCustomer.customerId}
          customerName={whatsAppCustomer.displayName || `#${whatsAppCustomer.customerId}`}
          phone={whatsAppCustomer.phone}
          email={customers.find(c => c.customer_id === whatsAppCustomer.customerId)?.email}
          onInteractionLogged={() => {
            pageCacheRef.current.clear();
            prefetchCacheRef.current.clear();
            clearTenantDocumentsLocalCache(user?.tenant_id);
            void loadDocuments(false);
          }}
          onClose={() => setWhatsAppCustomer(null)}
        />
      )}

      {whatsAppDocument && (
        <DocumentWhatsAppModal
          document={whatsAppDocument}
          phone={whatsAppDocument.customer_id ? findCustomerPhone(whatsAppDocument.customer_id) : null}
          onClose={() => setWhatsAppDocument(null)}
        />
      )}
    </div>
  );
}
